import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useDocumentPickerIfAvailable } from 'src/components/DocumentPicker'
import type { DocumentNode } from 'src/components/DocumentPicker'
import { LINKER_MODEL_IDS, Source } from '../../Linkers.constants'
import {
  SharepointItem,
  SharepointFile,
  SharepointShareUrlObject,
} from './SharepointFilePickerModal.types'
import { useSharepointLinker } from './SharepointFilePickerModal.hooks'

interface OnSubmitProps {
  caption: string
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

interface SharepointFilePickerContextProps {
  loading: boolean
  setLoading: (value: boolean) => void
  filesSelected: SharepointItem[]
  setFilesSelected: React.Dispatch<React.SetStateAction<SharepointItem[]>>
  onSubmit: (props: OnSubmitProps) => Promise<string[]>
  setSource: (source: Source) => void
  setSubjectIds: (subjectId: Array<string>) => void
  subjectIds: Array<string>
  shareUrlObject: SharepointShareUrlObject
  setShareUrlObject: React.Dispatch<
    React.SetStateAction<SharepointShareUrlObject>
  >
  documentLibraries: string[]
  selectedDocumentLibrary: string
  setSelectedDocumentLibrary: (documentLibrary: string) => void
  receptor?: ReceptorRecord
  setReceptor: (receptor: ReceptorRecord) => void
}

const SharepointFilePickerContext = createContext<
  SharepointFilePickerContextProps | undefined
>(undefined)

const DEFAULT_DOCUMENT_LIBRARY = 'Documents'

/**
 * A document picker node is keyed `siteId:driveId:itemId`, the same three
 * parts the FilePicker.aspx picker reports as sharepointIds.siteId,
 * parentReference.driveId and id, so onSubmit builds the import key it
 * always did.
 */
export const sharepointItemFromNode = (node: DocumentNode): SharepointItem => {
  const parts = node.id.split(':')
  const [siteId, driveId, itemId] =
    parts.length === 3 ? parts : ['', undefined, node.id]
  return {
    id: itemId,
    name: node.title,
    webUrl: node.url || '',
    sharepointIds: { siteId },
    parentReference: driveId ? { driveId } : undefined,
    size: node.sizeBytes || 0,
  }
}

const normalizeDocumentLibraryEntries = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean)
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
  }

  return []
}

const extractDocumentLibraries = (jsonString?: string): string[] => {
  if (!jsonString) return []
  try {
    const parsed = JSON.parse(jsonString)
    const rawEntries = parsed?.documentLibraries

    return normalizeDocumentLibraryEntries(rawEntries)
  } catch {
    return []
  }
}

const getDocumentLibrariesFromReceptor = (
  receptor?: ReceptorRecord,
): string[] => {
  if (!receptor) {
    return [DEFAULT_DOCUMENT_LIBRARY]
  }
  const credentialLibraries = extractDocumentLibraries(
    receptor.getCredentials(),
  )
  const configLibraries = extractDocumentLibraries(receptor.getConfig())
  const combined = [...credentialLibraries, ...configLibraries]
  const unique = Array.from(new Set(combined))

  return unique.length ? unique : [DEFAULT_DOCUMENT_LIBRARY]
}

export const SharepointFilePickerProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [filesSelected, setFilesSelected] = useState<SharepointItem[]>([])
  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<Array<string>>([])
  const [shareUrlObject, setShareUrlObject] =
    useState<SharepointShareUrlObject>({
      shareUrl: '',
      caption: '',
      isViaRequest: false,
    })
  const [documentLibraries, setDocumentLibraries] = useState<string[]>([
    DEFAULT_DOCUMENT_LIBRARY,
  ])
  const [selectedDocumentLibrary, setSelectedDocumentLibraryState] =
    useState<string>(DEFAULT_DOCUMENT_LIBRARY)
  const linkDocuments = useSharepointLinker()

  const [receptor, setReceptor] = useState<ReceptorRecord | undefined>(
    undefined,
  )

  const setSelectedDocumentLibrary = useCallback(
    (documentLibrary: string) => {
      setSelectedDocumentLibraryState(documentLibrary)
      setFilesSelected([])
    },
    [setFilesSelected],
  )

  useEffect(() => {
    const nextLibraries = getDocumentLibrariesFromReceptor(receptor)
    setDocumentLibraries(nextLibraries)
    setSelectedDocumentLibraryState((current) => {
      const updatedSelection = nextLibraries.includes(current)
        ? current
        : nextLibraries[0]
      if (updatedSelection !== current) {
        setFilesSelected([])
      }
      return updatedSelection
    })
  }, [receptor, setFilesSelected])

  const onSubmit = useCallback(
    async ({
      caption,
      relevantDate,
      isViaRequest = false,
    }: OnSubmitProps): Promise<string[]> => {
      if (!receptor) return []
      return await linkDocuments(
        receptor.toObject(),
        (filesSelected || []).map((item: SharepointItem) => ({
          id: item.id,
          display: item.name,
          webUrl: item.webUrl,
          type: source,
          subjectIds: subjectIds,
          siteId: item.sharepointIds.siteId,
          documentLibraryId: item.parentReference?.driveId,
          relevantDate,
        })) as SharepointFile[],
        isViaRequest,
        caption,
      )
    },
    [filesSelected, subjectIds, linkDocuments, receptor, source],
  )
  useEffect(() => {
    if (filesSelected.length === 0) {
      setLoading(true)
    }
  }, [filesSelected])

  // The document picker (accounts on NEW_DOCUMENT_PICKER) browses SharePoint
  // as the receptor's app registration and hands the picked nodes over
  // here, in the shape the linking flows expect. The connection browsed
  // becomes the one linked through.
  const documentPicker = useDocumentPickerIfAvailable()
  const selection = documentPicker?.selection
  const clearSelection = documentPicker?.clearSelection
  useEffect(() => {
    if (!selection || !clearSelection || selection.source === 'FolderScan')
      return
    if (selection.receptor.getModelid() !== LINKER_MODEL_IDS.SHAREPOINT) return
    setReceptor(selection.receptor)
    setFilesSelected(selection.nodes.map(sharepointItemFromNode))
    clearSelection()
  }, [selection, clearSelection])

  const contextValue = useMemo(
    () => ({
      loading,
      setLoading,
      filesSelected,
      setFilesSelected,
      onSubmit,
      setSource,
      setSubjectIds,
      subjectIds,
      shareUrlObject,
      setShareUrlObject,
      documentLibraries,
      selectedDocumentLibrary,
      setSelectedDocumentLibrary,
      receptor,
      setReceptor,
    }),
    [
      loading,
      filesSelected,
      onSubmit,
      subjectIds,
      shareUrlObject,
      documentLibraries,
      selectedDocumentLibrary,
      setSelectedDocumentLibrary,
      receptor,
    ],
  )

  return (
    <SharepointFilePickerContext.Provider value={contextValue}>
      {children}
    </SharepointFilePickerContext.Provider>
  )
}

export const useSharepointFilePickerContext =
  (): SharepointFilePickerContextProps => {
    const context = useContext(SharepointFilePickerContext)
    if (!context) {
      throw new Error(
        'useSharepointFilePicker must be used within a SharepointFilePickerProvider',
      )
    }
    return context
  }
